function cleanUpCanvas(canvas: HTMLCanvasElement) {
  // Set height and width to 0 because Safari does not garbage collect canvas
  // https://bugs.webkit.org/show_bug.cgi?id=195325
  canvas.width = 0;
  canvas.height = 0;

  // Most of the time we create canvases in memory and they shouldn't be attached
  // to the DOM anyway, but just calling this method just in case
  canvas.remove();
}

/**
 * This should be used instead of direct calls to document.createElement('canvas').
 * Canvases need to be manually cleaned up to avoid memory issues with WebKit.
 * WebKit considers this issue a wontfix and recommends manual cleanup.
 * Context: https://bugs.webkit.org/show_bug.cgi?id=195325#c14
 *
 * TODO: Once we upgrade to TS 5.2, we can use the `using` keyword for this.
 */
export function createCanvas(): [HTMLCanvasElement, () => void] {
  // This is the one valid usage.
  // eslint-disable-next-line rulesdir/document-create-element-canvas
  const canvas = document.createElement('canvas');
  const cleanup = () => cleanUpCanvas(canvas);
  return [canvas, cleanup];
}

/**
 * Wraps getImageData with handling for common edge cases.
 *
 * NOTE: It can be convenient to stub this function to return null to simulate
 * what happens when canvas errors occur.
 */
export function safeGetCanvasImageData(canvasBuffer: HTMLCanvasElement): ImageData | null {
  // Avoid IndexSizeError in getImageData
  if (canvasBuffer.width <= 0 || canvasBuffer.height <= 0) return null;

  try {
    const ctx = canvasBuffer.getContext('2d', { willReadFrequently: true });
    // getContext can return null in some circumstances (e.g. OOM) -- treat as low clarity
    if (ctx == null) return null;

    return ctx.getImageData(0, 0, canvasBuffer.width, canvasBuffer.height);
  } catch (e) {
    if (
      // OOM
      e instanceof RangeError ||
      // Browser incompatibility?
      e instanceof DOMException ||
      // OOM
      (e instanceof Error && e.name === 'NS_ERROR_OUT_OF_MEMORY')
    ) {
      return null;
    } else {
      throw e;
    }
  }
}
